/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import OptionWrapper from "../others/option-wrapper";
import theme from "../../theme/settings";
import { boxBorder, borderRadius4px, flexCol, flexRow, justifyEnd, overflowHidden, parseWidth, parseWidthHeight, positionAbsolute, positionRelative } from "../../styles/general";
import CardHeader from "./header";
import { CardFooter } from "./footer";
import { CardBody } from "./body";
import { getColor } from "../../styles/utils";
import { classNames } from "../../utils";
const {
  colors: {
    system: {
      white
    },
    line: {
      category
    }
  },
  spacing
} = theme;
const Card = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  body: bodyProp,
  bodyStyle,
  children,
  className,
  dividerColor,
  direction,
  footer,
  footerDivider,
  footerStyle,
  header,
  headerDivider,
  headerStyle,
  height,
  id,
  style,
  title,
  width
}, reference) => {
  const ref = useRef(null);
  const _CardCSS = CardCSS(width, height, headerDivider, footerDivider, dividerColor, direction);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const head = header || title ? jsx(CardHeader, {
    className: classNames(className === null || className === void 0 ? void 0 : className.header),
    style: headerStyle,
    title: title
  }, header) : null;
  const foot = footer ? jsx(CardFooter, {
    className: classNames(className === null || className === void 0 ? void 0 : className.footer),
    style: footerStyle
  }, footer) : null;
  const body = bodyProp || children && (head || foot) ? jsx(CardBody, {
    className: classNames(className === null || className === void 0 ? void 0 : className.body),
    style: bodyStyle
  }, bodyProp || children) : null;
  return useMemo(() => {
    return jsx("div", {
      id: id,
      ref: ref,
      css: _CardCSS,
      style: style,
      className: classNames(`DGN-UI-Card`, (className === null || className === void 0 ? void 0 : className.container) || className)
    }, head, body || children, foot);
  }, [bodyProp, bodyStyle, children, className, dividerColor, direction, footer, footerDivider, footerStyle, header, headerDivider, headerStyle, height, id, style, title, width]);
}));
const CardCSS = (width, height, headerDivider, footerDivider, dividerColor, direction) => css`
    ${direction === 'vertical' ? flexCol : flexRow};
    ${positionRelative};
    ${boxBorder};
    ${borderRadius4px};
    ${overflowHidden}
    ${parseWidthHeight(width || spacing([64.5]), height || 'max-content')};
    ${parseWidth(width)};
    background: ${white};
    .DGN-UI-Card-Header {
        ${flexRow};
        ${positionRelative};
        ${boxBorder};
        order: 1;
        padding: ${spacing([4])};
        &:after {
            ${positionAbsolute};
            content: '';
            left: 0;
            right: 0;
            bottom: 0;
            margin: ${spacing([0, 4])};
            border-bottom: ${headerDivider ? `1px solid ${getColor(dividerColor)}` : 'none'};
        }
    }
    .DGN-UI-Card-Body {
        ${flexCol};
        ${positionRelative};
        ${boxBorder};
        order: 2;
        padding: ${headerDivider ? spacing([4]) : spacing([0, 4, 4])};
    }
    .DGN-UI-Card-Footer {
        ${flexRow};
        ${justifyEnd};
        ${positionRelative};
        ${boxBorder};
        order: 3;
        padding: ${footerDivider ? spacing([4]) : spacing([0, 4, 4])};
        &:before {
            ${positionAbsolute};
            content: '';
            left: 0;
            right: 0;
            top: 0;
            margin: ${spacing([0, 4])};
            border-top: ${footerDivider ? `1px solid ${getColor(dividerColor)}` : 'none'};
        }
    }
    .DGN-UI-Card-Extra {
        ${flexCol};
        ${positionRelative};
        ${boxBorder};
    }
`;
Card.defaultProps = {
  bodyStyle: {},
  className: '',
  dividerColor: category,
  direction: 'vertical',
  footerDivider: false,
  footerStyle: {},
  headerDivider: true,
  headerStyle: {},
  style: {}
};
Card.propTypes = {
  /** The content of the component's body. */
  body: PropTypes.node,
  /** The content of the component's body. */
  bodyStyle: PropTypes.object,
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. ({container: '', header: '', body: '', footer: ''}) */
  className: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /** Color of divider. */
  dividerColor: PropTypes.string,
  /** The content of the component's footer. */
  footer: PropTypes.node,
  /** If `true`, will have divider between body and footer. */
  footerDivider: PropTypes.bool,
  /** Style inline of component's footer. */
  footerStyle: PropTypes.object,
  /** The content of the component's header. */
  header: PropTypes.node,
  /** If `true`, will have divider between header and body. */
  headerDivider: PropTypes.bool,
  /** Style inline of component's header. */
  headerStyle: PropTypes.object,
  /** Height of the component. */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Style inline of component. */
  style: PropTypes.object,
  /** The title of the component's header. */
  title: PropTypes.string,
  /** Width of the component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export { Card };
export default OptionWrapper(Card);